"use client";
import { Modal } from "@/ui/index";
import { Button } from "@cmp-src/primitives/button";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import { getAllResourcesIds } from "@gettactic/helpers/src/map/svg/getAllResourcesIds";
import { renameInteractiveZonesSvg } from "@gettactic/helpers/src/map/svg/renameInteractiveZonesSvg";
import { IResourceWithSchedules } from "@gettactic/hooks";
import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";

type Props = {
  onClose: () => void;
  editGroupId: string | null;
  resources: IResourceWithSchedules[];
  svg: SVGSVGElement;
  setMapChanges: React.Dispatch<React.SetStateAction<number>>;
};

type FormData = {
  resource: string;
};

export function EditMapResourceModal({
  editGroupId,
  setMapChanges,
  resources,
  onClose,
  svg
}: Props) {
  const [loading, setLoading] = useState(false);
  const usedIds = getAllResourcesIds(svg);

  const notAssigned = useMemo(
    () => ({ id: "unassigned", name: "Not assigned" }),
    []
  );

  const resourceItems = useMemo(() => {
    const items = resources
      ?.filter((x) => !usedIds.includes(x.id))
      .map((x) => ({
        id: x.id,
        name: x.name
      }));
    return [notAssigned, ...items];
  }, [resources, usedIds, notAssigned]);

  const defaultValue =
    resourceItems.find((x) => x.id === editGroupId)?.id || notAssigned.id;

  const form = useForm<FormData>({
    defaultValues: {
      resource: defaultValue
    }
  });

  const submitEdit = async (data: FormData) => {
    setLoading(true);
    let resource = data.resource;
    if (!editGroupId || !resource) {
      setLoading(false);
      return;
    }
    if (resource === "unassigned") {
      // for unassigned resources we need to
      // create a contrived not used reso id
      resource = `reso_unassigned_${Math.random()}`;
    }
    if (renameInteractiveZonesSvg(svg, editGroupId, resource)) {
      setMapChanges((x) => x + 1);
    }
    setLoading(false);
    onClose();
  };

  const unassignedCount = resourceItems.length - 1;

  return (
    <form onSubmit={form.handleSubmit(submitEdit)}>
      <div>
        <div className="col-span-12 mt-4 sm:col-span-6">
          <h3 className="mt-4 text-base font-bold text-neutral-800">
            Resource Assignment
          </h3>

          <p className="mb-2 text-base text-neutral-800">
            Assigning a resource makes it interactive. Marking something as
            "Not assigned" makes the item a static image i.e. you will not be
            able to interact with the element on the map.
          </p>
          <OptionsCountLabel
            count={unassignedCount}
            className="mb-2 text-sm text-neutral-500"
          />
          <HookFormUnifiedSelectInput
            form={form}
            name="resource"
            items={resourceItems}
            placeholder="Assign a resource to this map object..."
            searchable
            isRequired
            triggerClassName="w-full"
          />
        </div>
      </div>
      <Modal.Footer>
        <Button
          className="w-full sm:w-auto"
          variant="outline"
          onClick={onClose}
          type="button"
        >
          Cancel
        </Button>
        <Button
          className="mt-4 w-full sm:mt-0 sm:ml-3 sm:w-auto"
          type="submit"
          disabled={loading}
          isLoading={loading}
        >
          Save Changes
        </Button>
      </Modal.Footer>
    </form>
  );
}

const OptionsCountLabel = ({
  count,
  className
}: {
  count: number;
  className?: string;
}) => {
  if (count > 0) {
    return (
      <div className={className}>
        {count} Resource{count > 1 ? "s" : ""} Unassigned
      </div>
    );
  }

  return <div className={className}>All resources are already assigned.</div>;
};
